/**
 * Free-trial feature — API client + TanStack Query hooks.
 *
 * Mirrors the API contract exposed by `/api/v1/free-trial/status` from
 * `apps/api/src/routes/free-trial.ts`. Default cap is 10,000 conversions
 * across a 30-day window.
 *
 * Two wire-shapes are possible on the status endpoint:
 *   - `{ exists: false, tenant_id, cap, window_days }` — no trial row yet
 *   - `{ exists: true, tenant_id, started_at, conversion_count, cap,
 *        window_days, status, remaining, expires_at }` — live snapshot
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiClient } from "../../api-client";

export type FreeTrialStatusValue =
	| "active"
	| "expired_count"
	| "expired_time"
	| "closed";

export interface FreeTrialSnapshotMissing {
	exists: false;
	tenant_id: string;
	cap: number;
	window_days: number;
}

export interface FreeTrialSnapshotActive {
	exists: true;
	tenant_id: string;
	started_at: number;
	conversion_count: number;
	cap: number;
	window_days: number;
	status: FreeTrialStatusValue;
	remaining: number;
	expires_at: number;
}

export type FreeTrialSnapshot =
	| FreeTrialSnapshotMissing
	| FreeTrialSnapshotActive;

export const FREE_TRIAL_QUERY_KEY = ["free-trial", "status"] as const;

/** Fetch the current trial snapshot from the API. */
export function fetchFreeTrialStatus(): Promise<FreeTrialSnapshot> {
	return apiClient.get<FreeTrialSnapshot>("/free-trial/status");
}

/**
 * TanStack Query v5 hook. Refreshes on mount and every 60s while visible —
 * trial counters move faster than most dashboard data so a tight-ish
 * refetch interval keeps the progress bar honest without hammering the API.
 */
export function useFreeTrialStatus(): UseQueryResult<FreeTrialSnapshot, Error> {
	return useQuery({
		queryKey: FREE_TRIAL_QUERY_KEY,
		queryFn: fetchFreeTrialStatus,
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
}

// --------------------------------------------------------------------------
// Pure derivations — exported for the view layer + direct unit testing.
// --------------------------------------------------------------------------

export interface TrialDerived {
	/** Percentage of cap consumed, clamped 0..100. */
	percentUsed: number;
	/** Days remaining in the 30-day window, floored at zero. */
	daysRemaining: number;
	/** Whether the UI should surface the Upgrade CTA prominently. */
	nearCap: boolean;
	/** Human label for the current status. */
	statusLabel: string;
	/** ISO date string for expires_at, or null if unknown. */
	expiresAtIso: string | null;
}

const NEAR_CAP_THRESHOLD = 0.8;

export function deriveTrialView(
	snapshot: FreeTrialSnapshot,
	nowMs: number = Date.now(),
): TrialDerived {
	if (!snapshot.exists) {
		return {
			percentUsed: 0,
			daysRemaining: snapshot.window_days,
			nearCap: false,
			statusLabel: "Not started",
			expiresAtIso: null,
		};
	}

	const cap = Math.max(1, snapshot.cap);
	const used = Math.max(0, snapshot.conversion_count);
	const rawPct = (used / cap) * 100;
	const percentUsed = Math.max(0, Math.min(100, rawPct));

	const msRemaining = snapshot.expires_at - nowMs;
	const daysRemaining = Math.max(0, Math.floor(msRemaining / 86_400_000));

	const nearCap =
		snapshot.status !== "active" ||
		used / cap >= NEAR_CAP_THRESHOLD ||
		daysRemaining <= 3;

	const statusLabel =
		snapshot.status === "active"
			? "Active"
			: snapshot.status === "expired_count"
				? "Cap reached"
				: snapshot.status === "expired_time"
					? "Window elapsed"
					: "Closed";

	return {
		percentUsed,
		daysRemaining,
		nearCap,
		statusLabel,
		expiresAtIso: new Date(snapshot.expires_at).toISOString(),
	};
}
